/**
 * RepHospitalsView — sales-rep-scoped read-only view of THEIR assigned hospitals
 * with linked supplier orders + revenue + GP. Fed by GET /api/crm/my-hospitals.
 *
 * In-place actions (rep can do):
 *   - Add contact (mirrors to Hospital Portal automatically)
 *   - Send Email via Outlook (logs as CRM activity)
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Search, Mail, Phone, ExternalLink, MapPin, UserPlus, X, Send, ChevronDown, ChevronUp,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt$ = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const RepHospitalsView = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ hospitals: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [composerFor, setComposerFor] = useState(null); // hospital being emailed
  const [contactFor, setContactFor] = useState(null);   // hospital getting a new contact
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/api/crm/my-hospitals`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load my hospitals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() => {
    if (!search) return data.hospitals;
    const s = search.toLowerCase();
    return data.hospitals.filter((h) =>
      `${h.name} ${h.email || ""} ${h.phone || ""}`.toLowerCase().includes(s)
    );
  }, [data.hospitals, search]);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading my hospitals...</div>;

  return (
    <div className="space-y-5" data-testid="rep-hospitals-view">
      {/* === Stats === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat color="emerald" label="My Hospitals" v={data.count || 0} />
        <Stat color="blue"    label="Orders"        v={data.totals?.orders || 0} />
        <Stat color="purple"  label="Revenue"       v={fmt$(data.totals?.revenue)} />
        <Stat color="slate"   label="Lifetime $"    v={fmt$(data.totals?.lifetime_value)} />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        🔒 Read-only — these are the hospitals assigned to you. To modify contacts or details, open the account in the CRM tab.
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search my hospitals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="rep-hospitals-search"
        />
      </div>

      {filtered.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400">
          {data.count === 0 ? (
            <>
              <Building2 className="w-12 h-12 mx-auto opacity-40 mb-2" />
              <p className="font-bold">No hospitals assigned to you yet.</p>
              <p className="text-xs mt-1">Add a Hospital lead in the CRM tab — once approved, it'll appear here.</p>
            </>
          ) : <p>No matches for "{search}"</p>}
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((h) => {
          const isOpen = expanded === h.id;
          return (            <div key={h.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`rep-hospital-${h.id}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : h.id)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 className="w-4 h-4 text-rose-600 flex-shrink-0" />
                    <span className="font-bold text-slate-800 truncate">{h.name}</span>
                    {h.in_hospital_portal && <span className="text-[9px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">In Portal</span>}
                    {h.approval_status === "requested" && <span className="text-[9px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded animate-pulse">⏳ Awaiting</span>}
                    {h.status === "lead" && <span className="text-[9px] uppercase font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">Lead</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    {h.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{h.email}</span>}
                    {h.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{h.phone}</span>}
                    {(h.contacts || []).length > 0 && <span>· {h.contacts.length} contact{h.contacts.length === 1 ? "" : "s"}</span>}
                  </div>
                </div>
                <div className="text-right text-xs whitespace-nowrap">
                  <div className="font-bold text-slate-700">{fmt$(h.revenue)}</div>
                  <div className="text-slate-500">{h.order_count} order{h.order_count === 1 ? "" : "s"}</div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-3 text-sm">
                  {/* Action bar */}
                  <div className="flex flex-wrap gap-2 pb-2 border-b border-slate-200">
                    <Button size="sm" onClick={() => setContactFor(h)} variant="outline" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`add-contact-${h.id}`}>
                      <UserPlus className="w-3 h-3" /> Add Contact
                    </Button>
                    <Button size="sm" onClick={() => setComposerFor(h)} variant="outline" className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`send-email-${h.id}`}>
                      <Mail className="w-3 h-3" /> Send Email via Outlook
                    </Button>
                    <a
                      href={`/sales/dashboard?crm=${h.id}`}
                      onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("rep:open-crm", { detail: { accountId: h.id } })); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-100"
                      data-testid={`open-crm-${h.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Open in CRM
                    </a>
                  </div>

                  {h.billing_address && (
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">Billing Address</div>
                      <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 inline-flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5" />{h.billing_address}</pre>
                    </div>
                  )}

                  {h.contacts && h.contacts.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Contacts ({h.contacts.length})</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {h.contacts.map((c) => (
                          <div key={c.id} className="bg-white rounded border border-slate-200 px-2 py-1 text-xs">
                            <div className="font-bold">{c.name || c.email}{c.is_primary && <span className="ml-1 text-[8px] bg-emerald-100 text-emerald-700 px-1 rounded">PRIMARY</span>}</div>
                            <div className="text-slate-500">{c.title} {c.department ? `· ${c.department}` : ""}</div>
                            {c.email && <a href={`mailto:${c.email}`} className="text-blue-600">{c.email}</a>}
                            {c.phone && <div className="text-slate-500">{c.phone}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-[10px] uppercase font-bold text-slate-500">Recent Supplier Orders</div>
                      <div className="text-[10px] text-slate-500">GP: <strong className="text-emerald-700">{fmt$(h.gross_profit)}</strong> · Revenue: <strong>{fmt$(h.revenue)}</strong></div>
                    </div>
                    {h.orders.length === 0 ? (
                      <p className="text-xs italic text-slate-400">No orders yet for this hospital.</p>
                    ) : (
                      <div className="bg-white rounded border border-slate-200 divide-y">
                        {h.orders.slice(0, 10).map((o) => (
                          <div key={o.id} className="px-3 py-2 text-xs flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="font-mono font-semibold">{o.po_number || "(no PO)"}</div>
                              <div className="text-slate-500 truncate">{o.description}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold">{fmt$(o.hospital_invoice_amount)}</div>
                              <div className="text-slate-500">{o.status}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {contactFor && (
        <AddContactModal
          headers={headers}
          hospital={contactFor}
          onClose={() => setContactFor(null)}
          onSaved={() => { setContactFor(null); reload(); }}
        />
      )}
      {composerFor && (
        <SendEmailModal
          headers={headers}
          hospital={composerFor}
          onClose={() => setComposerFor(null)}
          onSent={() => { setComposerFor(null); reload(); }}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Add Contact modal — writes to /api/crm/accounts/{id}/contacts (auto-mirrors
// to Hospital Portal via bidirectional sync)
// -----------------------------------------------------------------------------
const AddContactModal = ({ headers, hospital, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", title: "", department: "", is_primary: false });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name && !form.email) {
      toast.error("Name or email required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/crm/accounts/${hospital.id}/contacts`, form, { headers });
      toast.success(`Contact added — synced to Hospital Portal`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="rep-add-contact-modal">
      <div className="bg-white rounded-lg w-full max-w-md my-16 shadow-2xl">
        <div className="border-b px-5 py-3 flex justify-between items-center">
          <h3 className="font-bold text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-emerald-600" /> Add Contact — {hospital.name}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-slate-500">
            Contacts you add here are saved to the CRM AND auto-synced to the Hospital Portal.
          </p>
          <Input placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rep-contact-name" />
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="rep-contact-email" />
          <Input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Title (e.g. CFO)" value={form.title} onChange={(e) => set("title", e.target.value)} />
            <Input placeholder="Department" value={form.department} onChange={(e) => set("department", e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => set("is_primary", e.target.checked)} />
            Make this the primary contact
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rep-contact-save">
            {saving ? "Saving..." : "Save Contact"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Send Email modal — composes mailto:, logs as CRM Email Outgoing activity
// -----------------------------------------------------------------------------
const SendEmailModal = ({ headers, hospital, onClose, onSent }) => {
  const [contactId, setContactId] = useState(
    (hospital.contacts || []).find((c) => c.is_primary)?.id || hospital.contacts?.[0]?.id || "",
  );
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const contact = (hospital.contacts || []).find((c) => c.id === contactId);
  const toEmail = contact?.email || hospital.email || "";

  const send = async () => {
    if (!toEmail) { toast.error("Pick a contact with an email"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/crm/activities`, {
        account_id: hospital.id,
        contact_id: contactId || null,
        contact_name: contact?.name || contact?.email || null,
        type: "email", direction: "outgoing",
        subject, body, status: "not_started",
      }, { headers });
      const url = `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = url;
      toast.success("Logged & opened in Outlook");
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="rep-send-email-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-5 py-3 flex justify-between items-center">
          <h3 className="font-bold text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" /> Email — {hospital.name}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-slate-500 bg-blue-50 border border-blue-200 rounded p-2">
            Composed here, opened in Outlook (mailto:), and auto-logged as an Email Outgoing activity in CRM.
          </p>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Send To</label>
            <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="rep-email-to">
              <option value="">— pick contact —</option>
              {(hospital.contacts || []).map((c) => (
                <option key={c.id} value={c.id}>{(c.name || c.email)} {c.email ? `· ${c.email}` : ""}</option>
              ))}
            </select>
            {toEmail && <div className="text-[11px] text-slate-500 mt-1">→ {toEmail}</div>}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="rep-email-subject" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Body</label>
            <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="rep-email-body" />
          </div>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={send} disabled={saving || !toEmail} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="rep-email-send">
            <Send className="w-4 h-4" /> Save & Open in Outlook
          </Button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ color, label, v }) => {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-lg p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{v}</div>
    </div>
  );
};

export default RepHospitalsView;
